import * as i0 from '@angular/core';
import { InjectionToken, NgModule, Optional, SkipSelf } from '@angular/core';
import { createServiceApi, serviceTypes, combineEffectsWithProviders } from '@essent/common';
export { DocumentType, FinancialItemStatus, FinancialItemType, PaymentMethod } from '@essent/common';
import * as i1 from '@ngrx/effects';
import { EffectsModule } from '@ngrx/effects';

const apiVersion$1 = '1';
const getFinancialItemsApi = createServiceApi('[Financial-itemsState] Get financial items', 'get', serviceTypes(), {
    getUrl: `/payment/financialitems/v${apiVersion$1}`,
    version: apiVersion$1,
    getParams: payload => payload
});

const { getFinancialItemsActions, getFinancialItems, getFinancialItemsSuccess, getFinancialItemsError, getFinancialItemsClear } = getFinancialItemsApi;

var DunningCostType;
(function (DunningCostType) {
    DunningCostType["SECOND_REMINDER_COSTS"] = "second_reminder_costs";
    DunningCostType["CONTRACT_BREACH_COSTS"] = "contract_breach_costs";
    DunningCostType["LAST_REMINDER_COSTS"] = "last_reminder_costs";
})(DunningCostType || (DunningCostType = {}));

var DunningLevel;
(function (DunningLevel) {
    DunningLevel["FIRST_REMINDER"] = "first_reminder";
    DunningLevel["SECOND_REMINDER"] = "second_reminder";
    DunningLevel["LAST_POSSIBILITY_TO_PAY"] = "last_possibility_to_pay";
    DunningLevel["END_OF_SUPPLY"] = "end_of_supply";
})(DunningLevel || (DunningLevel = {}));

var InstallmentStatus;
(function (InstallmentStatus) {
    InstallmentStatus["OPEN"] = "open";
    InstallmentStatus["CLEARED"] = "cleared";
    InstallmentStatus["DEACTIVATED"] = "deactivated";
})(InstallmentStatus || (InstallmentStatus = {}));

var PaymentLockReasonType;
(function (PaymentLockReasonType) {
    PaymentLockReasonType["WRITE_OFF"] = "write_off";
    PaymentLockReasonType["CORRECTION"] = "correction";
    PaymentLockReasonType["BAILIFF"] = "bailiff";
    PaymentLockReasonType["END_OF_SUPPLY"] = "end_of_supply";
    PaymentLockReasonType["BANKRUPTCY"] = "bankruptcy";
    PaymentLockReasonType["UNJUSTIFIED_SUPPLY"] = "unjustified_supply";
    PaymentLockReasonType["MANUAL_PAYMENT_INITIATED"] = "manual_payment_initiated";
    PaymentLockReasonType["CUSTOMER_DECEASED"] = "customer_deceased";
    PaymentLockReasonType["CONTRACT_BREACH"] = "contract_breach";
    PaymentLockReasonType["BANK_RETURN"] = "bank_return";
    PaymentLockReasonType["DEBT_RESTRUCTURING_ACT"] = "debt_restructuring_act";
})(PaymentLockReasonType || (PaymentLockReasonType = {}));

var MessageCode;
(function (MessageCode) {
    MessageCode["SOLD_ITEMS"] = "1041";
})(MessageCode || (MessageCode = {}));

var DunningLockReason;
(function (DunningLockReason) {
    DunningLockReason[DunningLockReason["WSNPWriteOff"] = 0] = "WSNPWriteOff";
    DunningLockReason[DunningLockReason["BankruptcyWriteOff"] = 1] = "BankruptcyWriteOff";
    DunningLockReason[DunningLockReason["DeceasedWriteOff"] = 2] = "DeceasedWriteOff";
    DunningLockReason[DunningLockReason["BailiffWriteOff"] = 3] = "BailiffWriteOff";
    DunningLockReason[DunningLockReason["NVVKWriteOff"] = 4] = "NVVKWriteOff";
    DunningLockReason[DunningLockReason["CoulanceWriteOff"] = 5] = "CoulanceWriteOff";
    DunningLockReason[DunningLockReason["CollectionCostsWriteOff"] = 6] = "CollectionCostsWriteOff";
    DunningLockReason[DunningLockReason["LittleAmountsNoAccountWriteOff"] = 7] = "LittleAmountsNoAccountWriteOff";
    DunningLockReason[DunningLockReason["CreditAmountsWriteOff"] = 8] = "CreditAmountsWriteOff";
    DunningLockReason[DunningLockReason["LiquidationWriteOff"] = 9] = "LiquidationWriteOff";
    DunningLockReason["DunningCostsForCollection"] = "A";
    DunningLockReason["LockReversalDirectDebit"] = "B";
    DunningLockReason["CorrectionInvoice"] = "C";
    DunningLockReason["Bailiff"] = "D";
    DunningLockReason["EndOfSupply"] = "E";
    DunningLockReason["Bankruptcy"] = "F";
    DunningLockReason["ComplaintsBoard"] = "G";
    DunningLockReason["InventarisationDebtCounseling"] = "I";
    DunningLockReason["Complaint"] = "K";
    DunningLockReason["Moratorium"] = "M";
    DunningLockReason["ProcessedByNVVK"] = "N";
    DunningLockReason["CorrectionForUnjustifiedSupply"] = "O";
    DunningLockReason["BreachOfContract"] = "P";
    DunningLockReason["Returns"] = "R";
    DunningLockReason["ProcessedBySHV"] = "S";
    DunningLockReason["WSNP"] = "W";
    DunningLockReason["UNKNOWN"] = "";
})(DunningLockReason || (DunningLockReason = {}));

var CollectionStep;
(function (CollectionStep) {
    CollectionStep["FIRST_REMINDER"] = "first_reminder";
    CollectionStep["SECOND_REMINDER"] = "second_reminder";
    CollectionStep["LAST_POSSIBILITY_TO_PAY"] = "last_possibility_to_pay";
    CollectionStep["END_OF_SUPPLY"] = "end_of_supply";
    CollectionStep["DEACTIVATION_INSTALLMENT_PLAN"] = "deactivation_installment_plan";
})(CollectionStep || (CollectionStep = {}));

const containsMessage = (response, code) => (response?.error?.errors || []).find((error) => error.code === code) !== undefined;

/**
 * @ignore
 */
const API = new InjectionToken('api-financial-items');

const apiVersion = '1';
const getFinancialItemsDocumentsApi = createServiceApi('[Financial-itemsState] Get financial items documents', 'get', serviceTypes(), {
    getUrl: `/payment/financialitems/documents/v${apiVersion}`,
    version: apiVersion,
    getParams: ({ accountId, contractGroupId, endDate, startDate }) => ({
        accountId,
        contractGroupId,
        endDate,
        startDate
    })
});

const { EFFECTS, PROVIDERS } = combineEffectsWithProviders(getFinancialItemsApi, getFinancialItemsDocumentsApi);
class FinancialItemsCoreModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('FinancialItemsCoreModule is imported more than once.');
        }
    }
    static forRoot(config) {
        return {
            ngModule: FinancialItemsCoreModule,
            providers: [
                {
                    provide: API,
                    useValue: config ? config.api : undefined
                }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialItemsCoreModule, deps: [{ token: FinancialItemsCoreModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: FinancialItemsCoreModule, imports: [i1.EffectsFeatureModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialItemsCoreModule, providers: PROVIDERS, imports: [EffectsModule.forFeature(EFFECTS)] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: FinancialItemsCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [EffectsModule.forFeature(EFFECTS)],
                    providers: PROVIDERS
                }]
        }], ctorParameters: () => [{ type: FinancialItemsCoreModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }] });

const { getFinancialItemsDocumentsActions, getFinancialItemsDocuments, getFinancialItemsDocumentsSuccess, getFinancialItemsDocumentsError, getFinancialItemsDocumentsClear } = getFinancialItemsDocumentsApi;

/**
 * Generated bundle index. Do not edit.
 */

export { API, CollectionStep, DunningCostType, DunningLevel, DunningLockReason, FinancialItemsCoreModule, InstallmentStatus, MessageCode, PaymentLockReasonType, containsMessage, getFinancialItems, getFinancialItemsActions, getFinancialItemsClear, getFinancialItemsDocuments, getFinancialItemsDocumentsActions, getFinancialItemsDocumentsClear, getFinancialItemsDocumentsError, getFinancialItemsDocumentsSuccess, getFinancialItemsError, getFinancialItemsSuccess };
